import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Layout, Radio, Spin, Alert, Button, Tooltip } from 'antd';
import Icon, { LoadingOutlined, PlayCircleOutlined, LockOutlined } from '@ant-design/icons';
import { ReactComponent as EditIcon } from '@src/assets/img/interface/node-icon.svg';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import WidgetGroupProcessor from '@kemu-io/kemu-core/widgets/widgetGroup/index.js';
import { HubServiceState, WidgetGroupState, WidgetState, WidgetType } from '@kemu-io/kemu-core/types';
import { useIntl, FormattedMessage } from 'react-intl';
import Canvas from '../../canvas/canvas';
// import 'react-reflex/styles.css';
import Breadcrumb from '../../breadcrumb/breadcrumb';
import styles from './LogicMapperSection.module.css';
import DesktopAppBanner, { DESKTOP_APP_BANNER_HEIGHT } from './DesktopAppBanner/DesktopAppBanner';
import portHelpers from './portHelpers';
import LogicMapperContextProvider, { LogicMapperContextInstance, ShowHelpConfig } from '@common/context/LogicMapperContext/LogicMapperContext';
import WidgetsBar from '@src/features/LogicMapper/WidgetsBar/WidgetsBar';
import WidgetLauncher from '@src/features/LogicMapper/WidgetLauncher/WidgetLauncher';
import { SelectedBlockInfo, StatelessWidgetsMap } from '@src/types/core_t';
import useJSPlumb from '@hooks/useJsPlumb';
import LogicMapper from '@src/features/LogicMapper/LogicMapper';
import HubApiKeyModal from '@components/HubApiKeyModal/HubApiKeyModal';
// import SplitPaneButtons from '@src/features/LogicMapper/SplitPaneButtons/SplitPaneButtons';
import {
  selectCurrentRecipeType,
  selectVisibleGroup,
  selectExecutionMode,
  selectEditingSnapshot,
  selectEditingModeWidgetIds,
  enterExecutionModeAction,
  exitExecutionModeAction,
} from '@src/features/Workspace/workspaceSlice';
import type { AppDispatch } from '@src/app/store';
import ContextMenu from '@src/features/LogicMapper/contextMenu/contextMenu';
import { SITE_LAYOUT_RIGHT_SECTION_CLASS, WIDGET_CONNECTION_CLASS , LM_CANVAS_CONTAINER_CLASS, VIRTUAL_PORT_ELEMENT_CLASS } from '@common/constants';
import RecipeWorkspaceTitle from '@components/RecipeWorkspaceTitle/RecipeWorkspaceTitle';
import {
  selectLogicMapperSettings,
  selectWidgetLauncherVisible,
  showWidgetLauncher as showWidgetLauncherAction,
} from '@src/features/interface/interfaceSlice';
import { forceLogicMapperRenderAction, selectCanvasZoom } from '@src/features/LogicMapper/logicMapperSlice';
import WidgetPortInspector from '@src/features/LogicMapper/WidgetPortInspector/WidgetPortInspector';
import DebuggingPanel from '@components/DebuggingPanel/DebuggingPanel';
import StatusPanel from '@components/StatusPanel/StatusPanel';
import GlobalVariablesPanel from '@components/GlobalVariablesPanel/GlobalVariablesPanel';
import LogicErrorNotification from '@src/features/LogicMapper/LogicErrorNotification/LogicErrorNotification';
import MinimizedPanelsContainer from '@components/MinimizedPanelsContainer/MinimizedPanelsContainer';
import { MinimizedPanelsProvider } from '@components/MinimizedPanelsContainer/MinimizedPanelsContext';
import ServiceHelpPanel from '@components/ServiceHelpPanel/ServiceHelpPanel';
import CustomWidgetModal from '@src/features/LogicMapper/customWidgetModal/customWidgetModal';
import ThirdPartyIntegrationCreationAssistant from '@src/features/LogicMapper/ThirdPartyIntegration/ThirdPartyIntegrationCreationAssistant';
import useHubSystemInfo from '@hooks/useHubSystemInfo';
import GlobalShortcuts from '@src/features/Shortcuts/GlobalShortcuts';
import MissingServicesModal from '@components/MissingServicesModal/MissingServicesModal';
import CreateSnippetModal from '@components/CreateSnippetModal/CreateSnippetModal';
import PremiumUpgradeModal from '@components/PremiumUpgradeModal/PremiumUpgradeModal';
import { getGateBody } from '@components/gates';
import useGetWidgetState from '@hooks/useGetWidgetState';
import useVirtualPortCleanup from '@hooks/useVirtualPortCleanup';
import useReconnectWidget from '@hooks/useReconnectWidget';
import { generateDomId } from '@common/utils';
import SnippetsDrawer from '@components/SnippetsDrawer/SnippetsDrawer';
import { getWidgetCanvasInfo, getGroupInnerWidgets, getBlockCanvasInfo, getCustomWidgetState } from '@src/app/recipe/utils';
import canvasViewportRegistry from '@common/canvasViewportRegistry';
import useHubApiKeyModal from '@hooks/useHubApiKeyModal';
import useTranslation from '@common/hooks/useTranslation';
import useConfirmModal from '@common/hooks/useConfirmModal';
import detectExecutionModeChanges from '@common/hooks/useExecutionModeChanges';

type Props = {
  visible: boolean;
  recipePoolId: string | null;
  openedBlock: SelectedBlockInfo | null;
  showMenuHelpConfig?: ShowHelpConfig | null;
}


const { Content } = Layout;

const LogicMapperSection = (props: Props): React.JSX.Element => {
  const { visible, recipePoolId, openedBlock, showMenuHelpConfig } = props;
  const dispatch = useDispatch<AppDispatch>();
  const visibleGroup = useSelector(selectVisibleGroup);
  const logicMapperSettings = useSelector(selectLogicMapperSettings);
  const widgetLauncherVisible = useSelector(selectWidgetLauncherVisible);
  const currentRecipeType = useSelector(selectCurrentRecipeType);
  const executionMode = useSelector(selectExecutionMode);
  const editingSnapshot = useSelector(selectEditingSnapshot);
  const editingModeWidgetIds = useSelector(selectEditingModeWidgetIds);
  const canvasZoom = useSelector(selectCanvasZoom);
  const getWidgetState = useGetWidgetState(recipePoolId);
  const sectionContainerRef = useRef<HTMLDivElement>(null);
  const systemInfo = useHubSystemInfo();
  const intl = useIntl();
  const t = useTranslation('LogicMapper');
  const [showHelpConfig, setShowHelpConfig] = useState<ShowHelpConfig | null>(null);
  const [drawingStatus, setDrawingStatus] = useState<LogicMapperContextInstance['drawingStatus']>('idle');
  const [switchingMode, setSwitchingMode] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const { shouldShow, dismiss } = useHubApiKeyModal();
  const [confirmModal, confirmModalContextHolder] = useConfirmModal();
  /** Stores the initial zoom level when the view loads (for reset functionality) */
  const initialZoomRef = useRef<number>(1);
  /** Local zoom value during slider drag for immediate UI feedback */
  const [dragZoom, setDragZoom] = useState<number | null>(null);

  const {
    containerEl: logicMapperContainerEl,
    initialize: initLogicMapperJsPlumb,
    jspInstance: logicMapperJsPlumb,
  } = useJSPlumb({ connectionClass: WIDGET_CONNECTION_CLASS });
  const { registerVirtualPort, cleanupAllVirtualPorts, unregisterVirtualPortByWidgetId } = useVirtualPortCleanup(logicMapperJsPlumb);
  const reconnectWidget = useReconnectWidget({ plumbInstance: logicMapperJsPlumb });

  const handleZoomChange = useCallback((zoom: number) => {
    if (logicMapperJsPlumb) {
      logicMapperJsPlumb.setZoom(zoom);
    }
  }, [logicMapperJsPlumb]);

  const handleShowHelp = useCallback((config: ShowHelpConfig | null) => {
    setShowHelpConfig(config);
  }, []);

  const handleBannerVisibilityChange = useCallback((visible: boolean) => {
    setBannerVisible(visible);
  }, []);

  const handleCloseWidgetLauncher = useCallback(() => {
    dispatch(showWidgetLauncherAction(false));
  }, [dispatch]);

  /**
   * Performs the actual exit from execution mode.
   */
  const performExitExecutionMode = useCallback(async () => {
    if (!recipePoolId) { return; }

    setSwitchingMode(true);
    try {
      // When exiting execution mode, clear all jsPlumb connections FIRST
      // They will be re-established based on the restored recipe data
      if (logicMapperJsPlumb) {
        logicMapperJsPlumb.deleteEveryConnection({ fireEvent: false });
        cleanupAllVirtualPorts();
      }
      await dispatch(exitExecutionModeAction({ recipePoolId })).unwrap();
      // Force LogicMapper to re-draw connections based on the restored recipe
      dispatch(forceLogicMapperRenderAction());
    } catch (error) {
      console.error('Error switching mode:', error);
    } finally {
      setSwitchingMode(false);
    }
  }, [dispatch, recipePoolId, logicMapperJsPlumb, cleanupAllVirtualPorts]);

  /**
   * Handles mode switching between editing and execution mode.
   * @param mode the mode to switch to.
   */
  const handleModeChange = useCallback(async (mode: string | number) => {
    if (!recipePoolId || switchingMode) { return; }

    const targetMode = mode === 'run';
    if (targetMode === executionMode) { return; }

    if (targetMode) {
      // Entering execution mode
      setSwitchingMode(true);
      try {
        await dispatch(enterExecutionModeAction({ recipePoolId })).unwrap();
      } catch (error) {
        console.error('Error switching mode:', error);
      } finally {
        setSwitchingMode(false);
      }
    } else {
      // Exiting execution mode - check for changes first
      const changes = detectExecutionModeChanges(recipePoolId, editingSnapshot, editingModeWidgetIds);

      if (changes.hasChanges) {
        // Show confirmation dialog
        confirmModal({
          title: t('ExecutionMode.ExitConfirmation.Title'),
          content: t('ExecutionMode.ExitConfirmation.Content'),
          type: 'warning',
          okText: t('ExecutionMode.ExitConfirmation.Proceed'),
          cancelText: t('ExecutionMode.ExitConfirmation.Cancel'),
          onOk: () => {
            performExitExecutionMode();
          },
        });
      } else {
        // No changes, exit directly
        performExitExecutionMode();
      }
    }
  }, [dispatch, recipePoolId, executionMode, switchingMode, editingSnapshot, editingModeWidgetIds, confirmModal, t, performExitExecutionMode]);

  useEffect(() => {
    setShowHelpConfig(showMenuHelpConfig || null);
  }, [showMenuHelpConfig]);

  // Capture the initial zoom level when the view loads or changes
  useEffect(() => {
    if (!recipePoolId || !openedBlock) { return; }

    let initialZoom = 1;
    if (visibleGroup?.groupId) {
      const widgetGroupState = getCustomWidgetState(recipePoolId, openedBlock.recipeId, visibleGroup.groupId);
      initialZoom = widgetGroupState.canvasZoom || 1;
    } else {
      const blockCanvasInfo = getBlockCanvasInfo(recipePoolId, openedBlock.recipeId);
      initialZoom = blockCanvasInfo.logicMapper?.zoom || 1;
    }

    initialZoomRef.current = initialZoom;
  }, [recipePoolId, openedBlock, visibleGroup?.groupId]);

  const ZOOM_MIN = 0.2;
  const ZOOM_MAX = 5;

  /**
   * Resets the canvas zoom to the initial zoom level captured when the view loaded.
   */
  const handleResetZoom = useCallback(() => {
    if (!recipePoolId || !openedBlock) { return; }

    const thingId = openedBlock.recipeId || 'workspace';
    canvasViewportRegistry.setViewport(recipePoolId, thingId, {
      zoom: initialZoomRef.current,
    });
  }, [recipePoolId, openedBlock]);

  /**
   * Calculates zoom value from mouse Y position relative to a track element.
   * @param trackElement - The slider track DOM element
   * @param clientY - The mouse Y coordinate
   * @returns The clamped zoom value
   */
  const calculateZoomFromPosition = useCallback((trackElement: HTMLElement, clientY: number): number => {
    const rect = trackElement.getBoundingClientRect();
    const offsetY = clientY - rect.top;
    const percentage = 1 - (offsetY / rect.height); // Inverted: top = max zoom
    const newZoom = ZOOM_MIN + percentage * (ZOOM_MAX - ZOOM_MIN);
    return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoom));
  }, []);

  /**
   * Handles mouse down on the zoom slider to start dragging.
   * @param e - The mouse down event
   */
  const handleZoomSliderMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!recipePoolId || !openedBlock) { return; }

    e.preventDefault();
    const trackElement = e.currentTarget;
    const thingId = openedBlock.recipeId || 'workspace';

    // Apply initial click
    const initialZoom = calculateZoomFromPosition(trackElement, e.clientY);
    setDragZoom(initialZoom);
    canvasViewportRegistry.setViewport(recipePoolId, thingId, { zoom: initialZoom });

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newZoom = calculateZoomFromPosition(trackElement, moveEvent.clientY);
      setDragZoom(newZoom);
      canvasViewportRegistry.setViewport(recipePoolId, thingId, { zoom: newZoom });
    };

    const handleMouseUp = () => {
      setDragZoom(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [recipePoolId, openedBlock, calculateZoomFromPosition]);

  /** Calculate thumb position as percentage (0 = bottom, 100 = top). Use local drag value for immediate feedback. */
  const displayZoom = dragZoom ?? canvasZoom;
  const zoomThumbPosition = ((displayZoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100;

  const drawVirtualPorts = useCallback((widgetId: string, widgetType: WidgetType) => {
    if (widgetType === WidgetType.widgetGroup) {
      const gateUI = getGateBody(widgetType);
      if (!gateUI || !openedBlock || !recipePoolId || !logicMapperJsPlumb) { return; }

      const widgetState = getWidgetState(widgetId);

      const widgetInputs = WidgetGroupProcessor.getInputNames(widgetState, {
        recipePoolId: recipePoolId,
        recipeType: currentRecipeType,
        thingRecipeId: openedBlock.recipeId,
        widgetId: widgetId
      });

      const widgetOutputs = WidgetGroupProcessor.getOutputNames(widgetState, {
        recipePoolId: recipePoolId,
        recipeType: currentRecipeType,
        thingRecipeId: openedBlock.recipeId,
        widgetId: widgetId,
      });

      for (const port of widgetOutputs) {
        if (!port.isInner) { continue; }
        const virtualPortInfo = portHelpers.buildVirtualPort({
          widgetId: widgetId,
          recipeId: recipePoolId,
          plumbInstance: logicMapperJsPlumb,
          port: port,
          portType: 'innerOutput',
          portDataType: port.type,
          isOutput: true,
          portName: port.name,
        });

        virtualPortInfo && registerVirtualPort({
          elementId: virtualPortInfo?.domElementId,
          widgetId: widgetId,
          portName: port.name,
        });
      }

      for (const port of widgetInputs) {
        if (!port.isInner) { continue; }
        const virtualPortInfo = portHelpers.buildVirtualPort({
          widgetId: widgetId,
          recipeId: recipePoolId,
          plumbInstance: logicMapperJsPlumb,
          port: port,
          portType: 'innerInput',
          portDataType: port.type,
          isOutput: false,
          portName: port.name,
        });

        virtualPortInfo && registerVirtualPort({
          elementId: virtualPortInfo?.domElementId,
          widgetId: widgetId,
          portName: port.name,
        });
      }
    }
  }, [logicMapperJsPlumb, registerVirtualPort]);

  const drawPortsForWidget = useCallback((widgetId: string, widgetType: WidgetType) => {
		const gateUI = getGateBody(widgetType);
		if (!gateUI || !openedBlock || !recipePoolId || !logicMapperJsPlumb) { return; }

		const widgetState = getWidgetState(widgetId);
		const gatePorts = gateUI.getPortsInformation(widgetState, {
			id: widgetId,
			thingRecipeId: openedBlock.recipeId,
			thingDbId: openedBlock.DbId,
      recipeId: recipePoolId,
			thingVersion: openedBlock.version,
			recipePoolId,
			recipeType: currentRecipeType,
		}, intl);

    const isWidgetGroup = widgetType === WidgetType.widgetGroup;
    const isHubService = widgetType === WidgetType.hubService;
    const hubService = isHubService ? (widgetState as HubServiceState)?.service : null;
    // Get the color of the hub service variant if it exists, otherwise use the service color
    const hubServiceColor = widgetState ? (hubService?.variants?.find((v) => v.id === widgetState.variantId)?.color ?? hubService?.color) : undefined;
    const widgetGroupColor = isWidgetGroup ? (widgetState as WidgetState<WidgetGroupState>).color : undefined;

    const widgetColor = isWidgetGroup ? widgetGroupColor : isHubService ? hubServiceColor : undefined;
		portHelpers.buildPorts({
			gateId: widgetId,
			recipeId: recipePoolId,
			plumbInstance: logicMapperJsPlumb,
			inputPorts: gatePorts.inputs,
			outputPorts: gatePorts.outputs,
			widgetType: widgetType,
      widgetColor,
      // widgetDisabled: !!widgetState.di
      // serviceOffline: !!widgetState.serviceOffline,
		});
	}, [recipePoolId, logicMapperJsPlumb, intl]);


  /**
   * Toggles the visibility of the ports of the given widget
   * @param widgetId - The id of the widget
   * @param visible - Whether to show the ports or not
   */
  const toggleWidgetPortsVisibility = useCallback((config: { widgetId: string, visible: boolean, inputsOnly?: boolean, outputsOnly?: boolean }) => {
    if (!logicMapperJsPlumb) { return; }
    if (!recipePoolId || !openedBlock) { return; }

    const { widgetId, visible, inputsOnly, outputsOnly } = config;
    const elementId = generateDomId(recipePoolId, openedBlock.recipeId, widgetId);
    const ports = logicMapperJsPlumb.getEndpoints(elementId);
    ports.forEach(port => {
      if (inputsOnly && port.isTarget) { return; }
      if (outputsOnly && port.isSource) { return; }
      if (visible) {
        port.removeClass('hidden');
      } else {
        port.addClass('hidden');
      }
    });
  }, [logicMapperJsPlumb, recipePoolId, openedBlock]);

  const removePortsAndConnections = useCallback((widgets: StatelessWidgetsMap) => {
    if (!logicMapperJsPlumb) { return; }
    if (!recipePoolId || !openedBlock) { return; }

    for (const widgetId in widgets) {
      const elementId = generateDomId(recipePoolId, openedBlock.recipeId, widgetId);
      logicMapperJsPlumb.deleteConnectionsForElement(elementId, { fireEvent: false });
      logicMapperJsPlumb.removeAllEndpoints(elementId);
      logicMapperJsPlumb.unmanage(elementId);
      unregisterVirtualPortByWidgetId(widgetId);
    }

    // Remove virtual ports
    // cleanupAllVirtualPorts();
	}, [logicMapperJsPlumb, recipePoolId, openedBlock, cleanupAllVirtualPorts]);


  /**
  * Returns widgets and virtual ports that are mostly contained within a parent widget.
  *
  * Calculates the parent widget's bounds and includes only items whose visible area
  * overlaps more than 50% with those bounds.
  * 
  * @param widgetId - The id of the widget
  * @param widgetState - The state of the widget 
  * @param widgetsMap - The map of widgets in the recipe
  * @returns An object containing the widgets and virtual ports within the parent widget's bounds
  */
  const getInnerItems = useCallback((
    widgetId: string,
    widgetState: WidgetState | null,
    widgetsMap: StatelessWidgetsMap
  ): { widgets: Array<{ id: string }>; virtualPorts: Array<{ domId: string }> } => {
    if (!widgetState || !recipePoolId || !openedBlock) {
      return { widgets: [], virtualPorts: [] };
    }

    const DEFAULT_WIDGET_SIZE = { width: 50, height: 50 };

    // Helper to extract widget size with type safety
    const getWidgetSize = (state: WidgetState | null | undefined): { width: number; height: number } => {
      if (state?.size && typeof state.size === 'object' && 'width' in state.size && 'height' in state.size) {
        return state.size as { width: number; height: number };
      }
      return DEFAULT_WIDGET_SIZE;
    };

    // Helper to calculate widget bounds from canvas info and size
    const calculateWidgetBounds = (
      canvasInfo: { position: { x: number; y: number } },
      size: { width: number; height: number }
    ) => ({
      left: canvasInfo.position.x,
      top: canvasInfo.position.y,
      right: canvasInfo.position.x + size.width,
      bottom: canvasInfo.position.y + size.height
    });

    // Calculate parent widget bounds
    const parentWidgetSize = getWidgetSize(widgetState);
    const parentCanvasInfo = getWidgetCanvasInfo(recipePoolId, openedBlock.recipeId, widgetId);
    const parentWidgetBounds = calculateWidgetBounds(parentCanvasInfo, parentWidgetSize);

    // Helper to calculate intersection area between two rectangles
    const getIntersectionArea = (
      rect1: { left: number; top: number; right: number; bottom: number },
      rect2: { left: number; top: number; right: number; bottom: number }
    ): number => {
      const left = Math.max(rect1.left, rect2.left);
      const top = Math.max(rect1.top, rect2.top);
      const right = Math.min(rect1.right, rect2.right);
      const bottom = Math.min(rect1.bottom, rect2.bottom);

      if (left >= right || top >= bottom) {
        return 0;
      }

      return (right - left) * (bottom - top);
    };

    // Helper to check if more than half of an item's area is within the parent widget's bounds
    const isMoreThanHalfWithinBounds = (
      itemBounds: { left: number; top: number; right: number; bottom: number }
    ): boolean => {
      const itemArea = (itemBounds.right - itemBounds.left) * (itemBounds.bottom - itemBounds.top);
      if (itemArea === 0) { return false; }

      const intersectionArea = getIntersectionArea(parentWidgetBounds, itemBounds);
      return intersectionArea > itemArea * 0.5;
    };

    // Get widgets (first try by groupId, then fallback to visual bounds)
    let widgets = getGroupInnerWidgets(recipePoolId, openedBlock.recipeId, widgetId);

    // If no widgets found by groupId, check for widgets visually inside the parent widget
    if (widgets.length === 0) {
      widgets = Object.values(widgetsMap).filter(widget => {
        if (widget.id === widgetId) { return false; }

        const childCanvasInfo = getWidgetCanvasInfo(recipePoolId, openedBlock.recipeId, widget.id);
        const childWidgetState = getWidgetState(widget.id);
        const childWidgetSize = getWidgetSize(childWidgetState);
        const childWidgetBounds = calculateWidgetBounds(childCanvasInfo, childWidgetSize);

        return isMoreThanHalfWithinBounds(childWidgetBounds);
      });
    }

    // Get virtual ports within the parent widget's bounds
    const virtualPorts: Array<{ domId: string }> = [];
    if (logicMapperJsPlumb) {
      const canvasContainer = document.querySelector(`.${LM_CANVAS_CONTAINER_CLASS}`);
      if (canvasContainer) {
        const virtualPortElements = document.querySelectorAll(`.${VIRTUAL_PORT_ELEMENT_CLASS}`);

        virtualPortElements.forEach(portEl => {
          if (!portEl.id) { return; }

          // Virtual ports use absolute positioning with CSS left/top in canvas coordinates
          const computedStyle = window.getComputedStyle(portEl);
          const portX = parseFloat(computedStyle.left) || 0;
          const portY = parseFloat(computedStyle.top) || 0;
          const rect = portEl.getBoundingClientRect();

          const portBounds = {
            left: portX,
            top: portY,
            right: portX + rect.width,
            bottom: portY + rect.height
          };

          // Check if more than half of the port is within note bounds
          if (isMoreThanHalfWithinBounds(portBounds)) {
            virtualPorts.push({ domId: portEl.id });
          }
        });
      }
    }

    return { widgets, virtualPorts };
  }, [recipePoolId, openedBlock, logicMapperJsPlumb, getWidgetState]);

  // Draw virtual ports for the given widgets
  const drawVirtualPortsForWidgets = useCallback((widgets: StatelessWidgetsMap) => {
    for (const widgetId in widgets) {
      const widget = widgets[widgetId];
      if (widget.type !== WidgetType.widgetGroup) { continue; }
      drawVirtualPorts(widgetId, widget.type);
    }
	}, [drawVirtualPorts]);


  // Draw ports and connections for the given widgets, including virtual ports and connections
  const drawPortsAndConnections = useCallback((widgets: StatelessWidgetsMap) => {
    for (const widgetId in widgets) {
      drawPortsForWidget(widgetId, widgets[widgetId].type);
    }

    drawVirtualPortsForWidgets(widgets);

    // setTimeout(() => {
    for (const widgetId in widgets) {
        reconnectWidget(widgetId);
      }
    // }, 1000);
  }, [drawPortsForWidget, drawVirtualPortsForWidgets, reconnectWidget]);

  // Removes all connections when the recipe changes
  // useLayoutEffect(() => {
  //   console.log('🔥 opening recipe', recipePoolId);
  //   if(logicMapperJsPlumb) {
  //     logicMapperJsPlumb.deleteEveryConnection({
  //       fireEvent: false,
  //     });

  //     // Remove all endpoints
  //     const endpoints = Object.values(logicMapperJsPlumb.endpointsByUUID);
  //     endpoints.forEach((endpoint) => {
  //       logicMapperJsPlumb.removeAllEndpoints(endpoint.element);
  //     });

  //     // Unmanage all managed elements
  //     const managedElements = Object.keys(logicMapperJsPlumb.getManagedElements());
  //     managedElements.forEach((elId) => {
  //       logicMapperJsPlumb.unmanage(elId);
  //     });

  //     cleanupAllVirtualPorts();
  //   }
  // }, [recipePoolId, logicMapperJsPlumb]);

  /* useEffect(() => {
    // Create an event listener for mouse wheel
    const handleMouseWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        if (logicMapperContainerEl && logicMapperJsPlumb) {
          const direction = e.deltaY > 0 ? -1 : 1;
          const ratio = 0.1;
          const zoom = zoomRef.current + direction * ratio;
          console.log('zoom', zoom);
          logicMapperJsPlumb.setZoom(zoom);
          logicMapperContainerEl.style.transform = `scale(${zoom})`;
          zoomRef.current = zoom;

          const canvasEl = logicMapperContainerEl.querySelector(`.${LOGIC_MAPPER_CANVAS_CLASS}`) as HTMLElement;
          if (canvasEl) {
            canvasEl.style.width = `${300 * zoom}%`;
            canvasEl.style.height = `${300 * zoom}%`;
          }
        }
      }
    };

    window.addEventListener('wheel', handleMouseWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleMouseWheel);
    };
  }, [logicMapperContainerEl, logicMapperJsPlumb]); */

  return (
    <LogicMapperContextProvider value={{
      jsPlumb: logicMapperJsPlumb,
      onShowHelp: handleShowHelp,
      drawingStatus,
      setDrawingStatus,
      logicMapperContainerEl: sectionContainerRef.current,
      systemInfo: systemInfo || null,
      // drawPortsForWidget,
      // drawVirtualPorts,
      // drawVirtualPortsForWidgets,

      drawPortsAndConnections,
      cleanupAllVirtualPorts,
      removePortsAndConnections,
      toggleWidgetPortsVisibility,
      getInnerItems
    }}>
      <div className={styles.Section}>
        {confirmModalContextHolder}
        {visible && (
          <Layout className={classNames(SITE_LAYOUT_RIGHT_SECTION_CLASS, styles.RightSide)}>
            <DesktopAppBanner onVisibilityChange={handleBannerVisibilityChange} />
            <SnippetsDrawer />

            <CustomWidgetModal />
            <ThirdPartyIntegrationCreationAssistant />

            <HubApiKeyModal
              visible={shouldShow}
              onClose={dismiss}
              onDismiss={dismiss}
            />

            {recipePoolId && (
              <WidgetsBar recipePoolId={recipePoolId}/>
            )}

            <div
              className={styles.ModeToggleContainer}
              style={bannerVisible ? { top: 150 + DESKTOP_APP_BANNER_HEIGHT } : undefined}
            >
              <Radio.Group
                className={styles.ModeToggleRadioGroup}
                value={executionMode ? 'run' : 'edit'}
                onChange={(e) => handleModeChange(e.target.value)}
                disabled={switchingMode}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="edit">
                  <span className={styles.ModeToggleRadioButtonInner}>
                    <Icon component={EditIcon} />
                    <span>{t('ExecutionMode.EditModeButton')}</span>
                  </span>
                </Radio.Button>
                <Radio.Button value="run">
                  <span className={styles.ModeToggleRadioButtonInner}>
                    <PlayCircleOutlined />
                    <span>{t('ExecutionMode.RunModeButton')}</span>
                  </span>
                </Radio.Button>
              </Radio.Group>
            </div>
            <Content
              ref={sectionContainerRef}
              className={classNames(
                styles.LayoutContent,
                styles.LogicMapperContainer, {
                  'custom-widget': !!visibleGroup
                }
              )
            }>
              {switchingMode && (
                <div className={styles.ModeSwitchingOverlay}>
                  <Spin indicator={<LoadingOutlined />} size="large" tip={t('ExecutionMode.SwitchingMode')} />
                </div>
              )}

              <MinimizedPanelsProvider>
                {showHelpConfig && (
                  <ServiceHelpPanel info={showHelpConfig} />
                )}

                {logicMapperSettings.showDebuggingPanel && (
                  <DebuggingPanel containerEl={sectionContainerRef.current} />
                )}

                {logicMapperSettings.showStatusPanel && (
                  <StatusPanel containerEl={sectionContainerRef.current} />
                )}

                {logicMapperSettings.showGlobalVariablesPanel && (
                  <GlobalVariablesPanel containerEl={sectionContainerRef.current} />
                )}

                <div className={styles.RecipeNameContainer}>
                  <RecipeWorkspaceTitle />
                </div>

                {logicMapperSettings.portInspectorVisible && (
                  <WidgetPortInspector containerEl={sectionContainerRef.current} />
                )}

                <MinimizedPanelsContainer containerEl={sectionContainerRef.current} />
              </MinimizedPanelsProvider>

              <LogicErrorNotification />

              <Breadcrumb />
              <GlobalShortcuts />
              <ContextMenu plumbInstance={logicMapperJsPlumb} />

              <MissingServicesModal />
              <CreateSnippetModal />

              <PremiumUpgradeModal />

              <WidgetLauncher
                visible={widgetLauncherVisible}
                onClose={handleCloseWidgetLauncher}
              />

              {/* <SplitPaneButtons /> */}
              {recipePoolId && openedBlock && (
                <Canvas
                  ref={initLogicMapperJsPlumb}
                  onZoomChange={handleZoomChange}
                  recipeId={recipePoolId}
                  selectedBlock={openedBlock}
                  loadingArea="logic-mapper"
                >
                  {logicMapperJsPlumb && (
                    <LogicMapper
                      containerEl={logicMapperContainerEl}
                      thingRecipeId={openedBlock.recipeId}
                      thingDbId={openedBlock.DbId}
                      thingVersion={openedBlock.version}
                      recipeId={recipePoolId}
                    />
                  )}

                  {/* <AnnotationsManager recipeId={recipePoolId} /> */}
                </Canvas>
              )}

              <div className={styles.ZoomIndicatorContainer}>
                <span className={styles.ZoomLevel}>{displayZoom.toFixed(1)}x</span>
                <div
                  className={styles.ZoomSliderTrack}
                  onMouseDown={handleZoomSliderMouseDown}
                >
                  <div className={styles.ZoomSliderFill} />
                  <div
                    className={styles.ZoomSliderThumb}
                    style={{ bottom: `${zoomThumbPosition}%` }}
                  />
                </div>
                <Tooltip title={t('ZoomIndicator.ResetTooltip')} mouseEnterDelay={0.5}>
                  <Button
                    size="small"
                    type="text"
                    onClick={handleResetZoom}
                    className={styles.ZoomResetButton}
                  >
                    {t('ZoomIndicator.ResetButton')}
                  </Button>
                </Tooltip>
              </div>

              {executionMode && (
                <Alert
                  banner
                  type="warning"
                  icon={<LockOutlined />}
                  showIcon
                  message={
                    <FormattedMessage
                      id="LogicMapper.ExecutionMode.WarningBanner"
                      values={{
                        execute: <strong>{t('ExecutionMode.RunModeButton')}</strong>,
                        compose: <strong>{t('ExecutionMode.EditModeButton')}</strong>,
                      }}
                    />
                  }
                  className={styles.ExecuteModeWarning}
                />
              )}
            </Content>
          </Layout>
        )}
      </div>
    </LogicMapperContextProvider>
  );
};

export default LogicMapperSection;
